import React, { useRef, useState } from "react";
import { Card, Button, Alert, Spinner, Badge } from "react-bootstrap";
import { uploadLogoClient, deleteLogoClient } from "./dataService";

/**
 * Logo de l'espace client, affiché en co-marquage dans l'en-tête de Marion
 * Livraison : le logo du client en grand, « avec Marion Livraison » en petit.
 *
 * Un geste distinct du formulaire d'édition : un fichier part immédiatement, en
 * multipart, dès qu'il est choisi.
 *
 * L'aperçu montre le logo stocké même quand le client ne le voit pas. La règle
 * d'affichage — un logo ET un compte en compte — vit au serveur ; la carte ne
 * fait que la dire, pour qu'on ne cherche pas pourquoi le client ne voit rien.
 */
const MESSAGE_PAR_DEFAUT = "Le logo n'a pas pu être enregistré.";

/** Le premier message utile d'une erreur de validation Laravel (422). */
const messageErreur = (e) =>
  e?.response?.data?.errors?.logo?.[0] ||
  e?.response?.data?.message ||
  MESSAGE_PAR_DEFAUT;

const LogoClientCard = ({ userId, urlInitiale, enCompte }) => {
  const [url, setUrl] = useState(urlInitiale || null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const champ = useRef(null);

  const envoyer = async (e) => {
    const fichier = e.target.files?.[0];
    // Le champ est vidé tout de suite : sans ça, rechoisir le même fichier après
    // une erreur ne déclencherait aucun événement `change`.
    e.target.value = "";

    if (!fichier) return;

    setEnvoi(true);
    setErreur(null);
    setSucces(null);

    try {
      const resultat = await uploadLogoClient(userId, fichier);
      setUrl(resultat.url);
      setSucces(
        resultat.affiche
          ? "Logo enregistré. Le client le verra à son prochain chargement de page."
          : "Logo enregistré. Il restera masqué tant que le compte n'est pas en compte."
      );
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  const retirer = async () => {
    if (!window.confirm("Retirer le logo de ce client ? Son espace affichera de nouveau le logo de Marion.")) {
      return;
    }

    setEnvoi(true);
    setErreur(null);
    setSucces(null);

    try {
      await deleteLogoClient(userId);
      setUrl(null);
      setSucces("Logo retiré.");
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="pb-2 d-flex align-items-center gap-2">
          Logo de l'espace client
          {url &&
            (enCompte ? (
              <Badge bg="success" className="fw-normal">affiché au client</Badge>
            ) : (
              <Badge bg="secondary" className="fw-normal">masqué</Badge>
            ))}
        </Card.Title>

        <p className="text-muted" style={{ fontSize: 13 }}>
          Affiché en co-marquage dans l'en-tête de Marion Livraison, sur tout
          l'espace connecté du client. PNG, JPEG ou WebP, 1 Mo au plus.
          {!enCompte && (
            <>
              {" "}
              <strong>Masqué tant que le compte n'est pas en compte</strong> —
              on peut l'envoyer dès maintenant pour préparer le passage.
            </>
          )}
        </p>

        {erreur && <Alert variant="danger" className="py-2">{erreur}</Alert>}
        {succes && <Alert variant="success" className="py-2">{succes}</Alert>}

        <div className="d-flex align-items-center gap-3 flex-wrap">
          {/* Fond clair et bordure : l'aperçu reproduit le fond blanc sur lequel
              l'en-tête pose le logo, y compris en mode sombre. */}
          <div
            className="d-flex align-items-center justify-content-center border rounded"
            style={{ width: 200, height: 72, background: "#ffffff", padding: 8 }}
          >
            {url ? (
              <img
                src={url}
                alt="Logo du client"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              />
            ) : (
              <span className="text-muted" style={{ fontSize: 12 }}>Aucun logo</span>
            )}
          </div>

          <input
            ref={champ}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={envoyer}
            style={{ display: "none" }}
          />

          <Button
            variant="outline-primary"
            size="sm"
            disabled={envoi}
            onClick={() => champ.current?.click()}
          >
            {envoi ? <Spinner animation="border" size="sm" /> : url ? "Remplacer" : "Envoyer un logo"}
          </Button>

          {url && (
            <Button variant="outline-danger" size="sm" disabled={envoi} onClick={retirer}>
              Retirer
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default LogoClientCard;
